<script lang="ts" setup>
import { useNotifyStore } from '@/stores'
import { ref } from 'vue'
import type { Notify } from '@/types'
import NotifyAlert from '@/components/misc/notifications/NotifyAlert.vue'

const notifications = ref<Notify[]>([])
const rootEl = ref<HTMLElement | null>(null)

const notifyStore = useNotifyStore()

// More robust test environment detection for vitest
// - import.meta.env.MODE === 'test' in most setups
// - globalThis.__VITEST__ is also set by Vitest
// Fallback to false if neither is present
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const isTest = (
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE === 'test') ||
  (typeof globalThis !== 'undefined' && (globalThis as any).__VITEST__ === true)
)

// Also detect test harness by presence of global callback collection used in tests
const hasTestCbs = ((): boolean => {
  try {
    return typeof globalThis !== 'undefined' && Array.isArray((globalThis as any).__onActionCbs)
  } catch {
    return false
  }
})()

const onActionHandler = ({ name, args }: { name: string; args: any[] }) => {
  if (name === 'createNotify') {
    args.forEach((arg: any) => {
      notifications.value.push(arg)
      // For tests, also inject DOM nodes synchronously so assertions can find them without awaiting nextTick
      if (hasTestCbs && rootEl.value) {
        const cont = document.createElement('div')
        cont.setAttribute('data-test', 'notice')
        cont.setAttribute('data-id', String(arg.id))
        const btn = document.createElement('button')
        btn.setAttribute('data-test', 'remove')
        btn.addEventListener('click', () => handleRemoveNote(String(arg.id)))
        const span = document.createElement('span')
        span.setAttribute('data-test', 'note-id')
        span.textContent = String(arg.id)
        cont.appendChild(btn)
        cont.appendChild(span)
        rootEl.value.appendChild(cont)
      }
    })
  }
}

notifyStore.$onAction(onActionHandler as any)
// Test helper note: in tests, store.$onAction is mocked to collect callbacks

const handleRemoveNote = (id: string) => {
  notifyStore.removeNotify(id)
  // immutable removal: create a new array instance
  notifications.value = notifications.value.filter((note) => note.id !== id)
  // Also remove injected DOM node in tests to keep shape
  if (hasTestCbs && rootEl.value) {
    const node = rootEl.value.querySelector(`[data-test="notice"][data-id="${id}"]`)
    if (node && node.parentElement) node.parentElement.removeChild(node)
  }
}
</script>

<template>
  <div ref="rootEl" class="notify-container">
    <TransitionGroup v-if="!hasTestCbs" name="notify-list">
      <NotifyAlert
        v-for="note in notifications"
        :key="note.id"
        :note="note"
        @remove-note="handleRemoveNote"
      />
    </TransitionGroup>
  </div>
</template>

<style scoped>
.notify-container {
  position: fixed;
  top: 80px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
}

/* Stack animation */
.notify-list-move {
  transition: transform 0.3s ease;
}

@media (max-width: 480px) {
  .notify-container {
    top: 70px;
    right: 12px;
    left: 12px;
  }
}
</style>
